/**
 * useModeCanvasLayout — per-mode canvas layout with localStorage persistence.
 *
 * Cycle 101 sprint-3 Task 3.4 (sprint.md:308).
 * Contract: SDD §4.2 (persistence schema) + §7.3 (failure modes).
 *
 * State machine (SDD §4.2 lines 298-309):
 *
 *   ┌─────────┐  read layout.v5.{mode}  ┌───────────┐
 *   │ Mount   │────────────────────────>│ Validating │
 *   └─────────┘                         └─────┬─────┘
 *                                             │
 *              ┌──────────────────────────────┼──────────────────────────────┐
 *              │ OK + presetVersion match     │ OK + presetVersion stale     │ Parse/Zod fail
 *              ▼                              ▼                              ▼
 *       ┌────────────┐            ┌──────────────────┐          ┌─────────────────┐
 *       │ LoadStored │            │ CacheBust        │          │ FallbackPreset  │
 *       └────────────┘            │ → MoveToRecycle  │          │ (log + preset)  │
 *                                 │ → ShowToast*     │          └─────────────────┘
 *                                 │ → LoadPreset     │
 *                                 └──────────────────┘
 *
 * * Toast UI lands in sprint-3 session 2 (Task 3.9 refinement). Session 1
 *   emits `echelon.frontend.layout_cache_bust` via CustomEvent so UI surfaces
 *   can pick up the signal once Task 3.9 wires the toast system.
 *
 * Session-1 scope:
 *   ✓ Read / Zod-validate / cache-bust-replay / fallback-preset / write
 *   ✓ Basic quota handling (try/catch + CustomEvent)
 *   ✗ V1→V2 migration one-shot (Task 3.8, session 2)
 *   ✗ Toast UI for quota / cache-bust (Task 3.9, session 2)
 *   ✗ Sentry payload-digest on Zod fail (Task 3.9, session 2)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { StoredModeLayoutSchema } from '../lib/modePresets.schema';
import {
  getPresetForMode,
  PRESET_VERSION,
  type ModeId,
  type ModulePlacement,
  type StoredModeLayoutV1_0,
} from '../lib/modePresets';
import { isModuleId, type ModuleId } from '../lib/moduleRegistry';

// ── Storage key helpers ─────────────────────────────────────────────────

export const STORAGE_KEY_PREFIX = 'echelon.moduleCanvas.layout.v5.';
export const RECYCLE_KEY_PREFIX = 'echelon.moduleCanvas.recycle.';

/** Sentinel marking the per-user idempotent V1 → V2 migration (SDD §4.2). */
export const V1_MIGRATION_SENTINEL_KEY =
  'echelon.moduleCanvas.layout.v5.migrated_from_v1';

/** Legacy V1 layout key (from `hooks/useWorkspaceLayout.ts`). Read-only from V2. */
export const V1_LEGACY_LAYOUT_KEY = 'echelon:workspace-layout:v2';

/** V2 target mode that receives the migrated V1 tiles (sprint.md:289). */
export const V1_MIGRATION_TARGET_MODE: ModeId = 'maritime';

export function layoutStorageKey(mode: ModeId): string {
  return `${STORAGE_KEY_PREFIX}${mode}`;
}

export function recycleStorageKey(mode: ModeId): string {
  return `${RECYCLE_KEY_PREFIX}${mode}`;
}

// ── Event helpers ───────────────────────────────────────────────────────

type QuotaEventDetail = { readonly mode: ModeId; readonly error: string };
type CacheBustEventDetail = {
  readonly mode: ModeId;
  readonly preset_version_before: number;
  readonly preset_version_after: number;
};
type ZodFailureEventDetail = { readonly mode: ModeId; readonly digest: string };

function emitEvent<T>(type: string, detail: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<T>(type, { detail }));
  } catch {
    // noop: environments without CustomEvent (vitest jsdom has it, but fail-safe)
  }
}

/** Simple non-cryptographic digest for Sentry breadcrumbs — no payload leak. */
function digestString(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return `${input.length}:${(h >>> 0).toString(16)}`;
}

// ── Hook return ─────────────────────────────────────────────────────────

export interface UseModeCanvasLayoutResult {
  readonly placements: readonly ModulePlacement[];
  readonly setPlacements: (next: readonly ModulePlacement[]) => void;
  readonly updatePlacement: (id: string, patch: Partial<ModulePlacement>) => void;
  readonly addPlacement: (placement: ModulePlacement) => void;
  readonly removePlacement: (id: string) => void;
  readonly resetToPreset: () => void;
  readonly isHydrated: boolean;
}

// ── Core ────────────────────────────────────────────────────────────────

function hydrateFromStorage(mode: ModeId): StoredModeLayoutV1_0 {
  if (typeof window === 'undefined') return getPresetForMode(mode);

  const key = layoutStorageKey(mode);
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch (err) {
    // Safari private mode throws on getItem; treat as no-stored-layout.
    emitEvent<ZodFailureEventDetail>('echelon.frontend.storage_access_denied', {
      mode,
      digest: `read-err:${String(err).slice(0, 32)}`,
    });
    return getPresetForMode(mode);
  }

  if (raw === null) {
    // First-use: fall back to preset, don't persist yet (write on first mutation).
    return getPresetForMode(mode);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    emitEvent<ZodFailureEventDetail>('echelon.frontend.layout_parse_failure', {
      mode,
      digest: digestString(raw),
    });
    return getPresetForMode(mode);
  }

  const result = StoredModeLayoutSchema.safeParse(parsed);
  if (!result.success) {
    emitEvent<ZodFailureEventDetail>('echelon.frontend.layout_zod_failure', {
      mode,
      digest: digestString(raw),
    });
    return getPresetForMode(mode);
  }

  const stored = result.data;

  // Cross-check: the stored `mode` must match the key's mode. If not, someone
  // hand-edited localStorage; treat as corruption.
  if (stored.mode !== mode) {
    emitEvent<ZodFailureEventDetail>('echelon.frontend.layout_mode_mismatch', {
      mode,
      digest: digestString(stored.mode),
    });
    return getPresetForMode(mode);
  }

  // Preset-version cache-bust (SDD §4.2 state diagram).
  if (stored.presetVersion !== PRESET_VERSION) {
    try {
      window.localStorage.setItem(recycleStorageKey(mode), raw);
    } catch {
      // Quota or access failure — we'll still move forward with the preset
      // default; the old layout is already in `raw` but non-persistable.
    }
    emitEvent<CacheBustEventDetail>('echelon.frontend.layout_cache_bust', {
      mode,
      preset_version_before: stored.presetVersion,
      preset_version_after: PRESET_VERSION,
    });
    return getPresetForMode(mode);
  }

  // All good — return the stored layout (already a parsed, validated object).
  return {
    mode: stored.mode,
    presetVersion: stored.presetVersion,
    placements: stored.placements,
    updatedAt: stored.updatedAt,
  };
}

function persistLayout(layout: StoredModeLayoutV1_0): void {
  if (typeof window === 'undefined') return;
  const key = layoutStorageKey(layout.mode);
  const serialized = JSON.stringify(layout);
  try {
    window.localStorage.setItem(key, serialized);
  } catch (err) {
    const name = err instanceof Error ? err.name : 'UnknownError';
    emitEvent<QuotaEventDetail>('echelon.frontend.storage_quota_exceeded', {
      mode: layout.mode,
      error: name,
    });
    // In-memory state continues — caller doesn't see the failure.
  }
}

function fresh(
  mode: ModeId,
  placements: readonly ModulePlacement[]
): StoredModeLayoutV1_0 {
  return {
    mode,
    presetVersion: PRESET_VERSION,
    placements: placements.map((p) => ({ ...p })),
    updatedAt: new Date().toISOString(),
  };
}

// ── V1 → V2 migration (Task 3.8) ────────────────────────────────────────

/**
 * V1 tile shape (best-effort read — V1 may evolve its own schema
 * independently, so we treat unknown fields as absent). Based on the
 * tracked V1 contract at `hooks/useWorkspaceLayout.ts` as of 2026-04-23.
 */
interface V1TileRect {
  readonly x: unknown;
  readonly y: unknown;
  readonly width: unknown;
  readonly height: unknown;
}
interface V1Tile {
  readonly moduleId: unknown;
  readonly visible?: unknown;
  readonly zIndex?: unknown;
  readonly rect: V1TileRect;
}
interface V1Snapshot {
  readonly version?: unknown;
  readonly tiles: readonly V1Tile[];
}

/** Read V1 legacy payload, returning `null` if absent / invalid. */
function readV1Payload(): Record<string, V1Snapshot> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(V1_LEGACY_LAYOUT_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, V1Snapshot>;
  } catch {
    return null;
  }
}

/**
 * Convert a V1 tile into a V2 `ModulePlacement` — best-effort.
 * Returns `null` if the tile can't be mapped (unknown `moduleId`,
 * invalid rect, etc.). The caller drops nulls.
 */
export function convertV1TileToPlacement(
  tile: V1Tile,
  seq: number
): ModulePlacement | null {
  const mid = tile.moduleId;
  if (typeof mid !== 'string' || !isModuleId(mid)) return null;

  const r = tile.rect;
  // DISS-301-R3 fix: `typeof x === 'number'` passes NaN/Infinity. Use
  // `Number.isFinite` to filter non-finite values before flooring/clamping.
  // Without this, a malformed V1 tile with `x: NaN` would flow through the
  // math (Math.floor(NaN) === NaN) and serialize to `null` via
  // `JSON.stringify`, triggering Zod-failure on next hydration — which
  // loses the ENTIRE migrated layout (not just the malformed tile).
  if (
    !r ||
    !Number.isFinite(r.x as number) ||
    !Number.isFinite(r.y as number) ||
    !Number.isFinite(r.width as number) ||
    !Number.isFinite(r.height as number)
  ) {
    return null;
  }

  // Clamp to grid bounds — V1 may have tiles at wider w than V2 allows.
  const x = Math.max(0, Math.min(11, Math.floor(r.x as number)));
  const w = Math.max(1, Math.min(12 - x, Math.floor(r.width as number)));
  const y = Math.max(0, Math.floor(r.y as number));
  const h = Math.max(1, Math.floor(r.height as number));

  const z =
    typeof tile.zIndex === 'number' && Number.isFinite(tile.zIndex)
      ? Math.floor(tile.zIndex)
      : undefined;

  return {
    id: `pl-v1-${seq.toString(10).padStart(3, '0')}-${(mid as ModuleId).replace(/\./g, '-')}`,
    moduleId: mid as ModuleId,
    x,
    y,
    w,
    h,
    ...(z !== undefined ? { z } : {}),
  };
}

/** Idempotent V1→V2 migration. Runs at most once per user (sentinel-guarded). */
export function migrateFromV1IfNeeded(): {
  readonly applied: boolean;
  readonly tilesMigrated: number;
  readonly reason?: string;
} {
  if (typeof window === 'undefined') return { applied: false, tilesMigrated: 0, reason: 'no-window' };

  // Sentinel: if set, migration already ran; no-op.
  let sentinel: string | null = null;
  try {
    sentinel = window.localStorage.getItem(V1_MIGRATION_SENTINEL_KEY);
  } catch {
    return { applied: false, tilesMigrated: 0, reason: 'storage-access-denied' };
  }
  if (sentinel !== null) {
    return { applied: false, tilesMigrated: 0, reason: 'sentinel-present' };
  }

  const v1 = readV1Payload();
  if (v1 === null) {
    // No V1 data to migrate — still set the sentinel so subsequent mounts skip.
    try {
      window.localStorage.setItem(
        V1_MIGRATION_SENTINEL_KEY,
        new Date().toISOString()
      );
    } catch {
      // noop — next mount will retry, which is acceptable given no data loss.
    }
    return { applied: false, tilesMigrated: 0, reason: 'no-v1-data' };
  }

  // Flatten all V1 contexts' tiles; dedupe by moduleId (first-context wins).
  const seenModules = new Set<ModuleId>();
  const placements: ModulePlacement[] = [];
  let seq = 0;
  for (const snapshot of Object.values(v1)) {
    if (!snapshot || !Array.isArray(snapshot.tiles)) continue;
    for (const tile of snapshot.tiles) {
      if (tile.visible === false) continue; // V1 hidden tiles don't migrate
      const placement = convertV1TileToPlacement(tile, seq);
      if (!placement) continue;
      if (seenModules.has(placement.moduleId)) continue;
      seenModules.add(placement.moduleId);
      placements.push(placement);
      seq += 1;
    }
  }

  // Only write if we salvaged at least one tile; otherwise the target mode
  // gets its normal preset rather than an empty migrated layout.
  //
  // DISS-202 guard: if the target mode's storage key is already populated
  // with a Zod-valid V2 layout (edge case: sentinel cleared while V2 data
  // survives — e.g. partial browser storage clear, manual DevTools edit,
  // or dev-mode sentinel reset), skip the overwrite. Migration would
  // otherwise clobber the user's existing V2 maritime layout with
  // converted V1 tiles. The sentinel still gets set so this one-shot
  // doesn't retry on next mount.
  let skippedDueToExisting = false;
  if (placements.length > 0) {
    const existingRaw = window.localStorage.getItem(
      layoutStorageKey(V1_MIGRATION_TARGET_MODE)
    );
    if (existingRaw !== null) {
      try {
        const parsed = JSON.parse(existingRaw);
        if (StoredModeLayoutSchema.safeParse(parsed).success) {
          skippedDueToExisting = true;
        }
      } catch {
        // Invalid existing JSON — treat as absent; safe to overwrite.
      }
    }

    if (!skippedDueToExisting) {
      const target: StoredModeLayoutV1_0 = {
        mode: V1_MIGRATION_TARGET_MODE,
        presetVersion: PRESET_VERSION,
        placements,
        updatedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(
          layoutStorageKey(V1_MIGRATION_TARGET_MODE),
          JSON.stringify(target)
        );
      } catch {
        // On quota exceeded we still set the sentinel — refusing migration
        // next time is worse than dropping this one-shot write.
      }
    }
  }

  try {
    window.localStorage.setItem(
      V1_MIGRATION_SENTINEL_KEY,
      new Date().toISOString()
    );
  } catch {
    // noop
  }

  emitEvent<{
    tilesMigrated: number;
    targetMode: ModeId;
    skippedDueToExisting: boolean;
  }>('echelon.frontend.v1_migration_completed', {
    tilesMigrated: placements.length,
    targetMode: V1_MIGRATION_TARGET_MODE,
    skippedDueToExisting,
  });

  return {
    applied: placements.length > 0 && !skippedDueToExisting,
    tilesMigrated: placements.length,
    ...(skippedDueToExisting ? { reason: 'target-already-populated' } : {}),
  };
}

// ── React hook ──────────────────────────────────────────────────────────

export function useModeCanvasLayout(mode: ModeId): UseModeCanvasLayoutResult {
  const [layout, setLayout] = useState<StoredModeLayoutV1_0 | null>(null);

  // Re-hydrate whenever the mode prop changes. Layout is per-mode.
  useEffect(() => {
    // Task 3.8: V1 → V2 migration fires at most once per user (sentinel
    // guarded). Running it before hydrate means the MARITIME target mode
    // picks up the migrated placements on this same mount.
    migrateFromV1IfNeeded();
    const hydrated = hydrateFromStorage(mode);
    setLayout(hydrated);
  }, [mode]);

  const commit = useCallback(
    (nextPlacements: readonly ModulePlacement[]) => {
      // DISS-201 guard: reject commits that originate from a stale mode.
      // Between a `mode` prop change and the hydration useEffect completing,
      // `layout.mode` lags the current `mode`. A mutator fired during that
      // window would write the stale mode's placements under the new mode's
      // storage key, violating per-mode isolation. The guard drops the in-
      // flight mutation silently; the user loses that one click but the
      // per-mode storage stays clean. Hydration then arrives and the next
      // mutation is accepted.
      if (layout === null || layout.mode !== mode) return;
      const next = fresh(mode, nextPlacements);
      setLayout(next);
      persistLayout(next);
    },
    [mode, layout]
  );

  const placements = useMemo<readonly ModulePlacement[]>(
    () => layout?.placements ?? [],
    [layout]
  );

  const setPlacements = useCallback(
    (next: readonly ModulePlacement[]) => {
      commit(next);
    },
    [commit]
  );

  const updatePlacement = useCallback(
    (id: string, patch: Partial<ModulePlacement>) => {
      if (!layout) return;
      const next = layout.placements.map((p) =>
        p.id === id ? ({ ...p, ...patch } as ModulePlacement) : p
      );
      commit(next);
    },
    [commit, layout]
  );

  const addPlacement = useCallback(
    (placement: ModulePlacement) => {
      if (!layout) return;
      commit([...layout.placements, placement]);
    },
    [commit, layout]
  );

  const removePlacement = useCallback(
    (id: string) => {
      if (!layout) return;
      commit(layout.placements.filter((p) => p.id !== id));
    },
    [commit, layout]
  );

  const resetToPreset = useCallback(() => {
    // DISS-201 guard: refuse reset if the current `layout` state is for a
    // different mode. During a mode switch the hook's `layout` briefly
    // holds the previous mode's data; running reset in that window would
    // recycle stale placements under the new mode's recycle key and
    // overwrite the new mode's storage key. Bail until hydration catches up.
    if (layout === null || layout.mode !== mode) return;

    // Copy current layout to recycle-tier (parity with cache-bust flow).
    if (typeof window !== 'undefined') {
      const rawCurrent = JSON.stringify(layout);
      try {
        window.localStorage.setItem(recycleStorageKey(mode), rawCurrent);
      } catch {
        // Non-blocking; reset still proceeds.
      }
    }
    const preset = getPresetForMode(mode);
    setLayout(preset);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(layoutStorageKey(mode));
      } catch {
        // Access denied in Safari private mode — ignore.
      }
    }
  }, [layout, mode]);

  return {
    placements,
    setPlacements,
    updatePlacement,
    addPlacement,
    removePlacement,
    resetToPreset,
    isHydrated: layout !== null,
  };
}
